import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faCheck, faTimes, faEdit, faEye } from '@fortawesome/free-solid-svg-icons';

import { formatDateAsString, removeCamelCase } from '../../../util/StringUtil';
import { alphaNumericSort } from '../../../util/SortUtil';

import { Pagination } from './pagination/Pagination';
import { TableFilter } from './table-filter/TableFilter';
import { TableSearch } from './table-search/TableSearch';
import { StandardButton } from '../button/standard-button/StandardButton';

import './Table.css';

export const Table = ({
  tableData,
  columnWidths,
  searchEnabled,
  filterEnabled,
  onRowClick,
  buttonText,
  onButtonClick,
  buttonDisabled,
  buttonOneWidth,
  buttonTwoWidth,
  buttonTwoText,
  onButtonTwoClick,
  buttonTwoDisabled,
  hiddenColumns,
  noResultsMessage,
  tableTitle,
  buttonThreeText,
  onButtonThreeClick,
  buttonThreeDisabled,
  maxRowCount,
  actionColumnEnabled,
  actionOneClick,
  actionTwoClick,
  horizontalScroll,
  dropdownOptions,
  dropdownOption,
  onDropdownChange,
  getDropdownOptionLabel,
  getDropdownOptionValue,
  stickyFirstRow,
  applyStyleChange,
  skipInitialSort,
  excludedFilterColumns = [],
  highlightExpression,
  highlightColor,
}) => {
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [tableDataPages, setTableDataPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [selectedRow, setSelectedRow] = useState({ index: 0, page: 0 });

  const sortKey = useRef(null);
  const sortDirection = useRef(null);
  const initialSortDone = useRef(false);

  const ACTIONS_COL_WIDTH = '90px';

  const performInitialSort = useCallback(
    (data) => {
      if (!data || data.length === 0) return [];

      const row = data[0];

      for (const [key] of Object.entries(row)) {
        if (!hiddenColumns.includes(key)) {
          sortKey.current = key;
          break;
        }
      }

      data.sort((objectOne, objectTwo) => alphaNumericSort(objectOne, objectTwo, sortKey.current));
      sortDirection.current = 'asc';
      initialSortDone.current = true;
      return data;
    },
    [hiddenColumns]
  );

  useEffect(() => {
    const copyOfTableData = [...tableData];

    if (stickyFirstRow) {
      copyOfTableData.shift();
    }

    if (!skipInitialSort && !initialSortDone.current) {
      performInitialSort(copyOfTableData);
    } else if (initialSortDone.current) {
      copyOfTableData.sort((objectOne, objectTwo) => alphaNumericSort(objectOne, objectTwo, sortKey.current));
      if (sortDirection.current === 'desc') {
        copyOfTableData.reverse();
      }
    }

    setFilteredTableData(copyOfTableData);
    setCurrentPage(0);
    setSelectedRow({ index: 0, page: 0 });
  }, [tableData, skipInitialSort, stickyFirstRow, performInitialSort]);

  useEffect(() => {
    let copyOfFilteredTableData = [...filteredTableData];
    const pages = [];
    while (copyOfFilteredTableData.length) {
      pages.push(copyOfFilteredTableData.splice(0, stickyFirstRow ? maxRowCount - 1 : maxRowCount));
    }
    setTableDataPages([...pages]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTableData]);

  const handleHeaderClick = (key) => {
    let copyOfFilteredTableData = [...filteredTableData];

    if (key !== sortKey.current) {
      sortKey.current = key;
      sortDirection.current = 'asc';
      copyOfFilteredTableData.sort((objectOne, objectTwo) => alphaNumericSort(objectOne, objectTwo, key));
    } else {
      sortDirection.current = sortDirection.current === 'asc' ? 'desc' : 'asc';
      copyOfFilteredTableData.reverse();
    }

    setFilteredTableData(copyOfFilteredTableData);
    setCurrentPage(0);
  };

  const handleRowClick = (object, index) => {
    if (onRowClick) {
      onRowClick(object);
    }
    setSelectedRow({ index, page: currentPage });
  };

  const handleActionOneClick = (object, event) => {
    event.stopPropagation();
    if (actionOneClick) {
      actionOneClick(object);
    }
  };

  const handleActionTwoClick = (object, event, index) => {
    event.stopPropagation();
    if (actionTwoClick) {
      actionTwoClick(object);
    }
    setSelectedRow({ index, page: currentPage });
  };

  const buildTableHeader = () => {
    if (filteredTableData.length === 0) return null;

    const objectKeys = Object.keys(filteredTableData[0]);

    const headers = objectKeys.map((key, index) => {
      if (!hiddenColumns.includes(key) && !filteredColumns.includes(key)) {
        const customWidth = columnWidths && columnWidths[key];

        const columnStyle = {
          width: customWidth || undefined,
          minWidth: horizontalScroll && columnWidths ? columnWidths[key] : undefined,
        };

        return (
          <th key={index} style={columnStyle} onClick={() => handleHeaderClick(key)}>
            <div className="table-header-content">
              <div className="table-header-text">{removeCamelCase(key)}</div>
              <div className="table-header-icon">
                <FontAwesomeIcon icon={faSort} />
              </div>
            </div>
          </th>
        );
      }
      return null;
    });

    if (actionColumnEnabled) {
      headers.push(
        <th key="actions" style={{ width: '90px', minWidth: '90px' }}>
          Actions
        </th>
      );
    }

    return (
      <thead className="table-header">
        <tr className="table-header-row">{headers}</tr>
      </thead>
    );
  };

  const buildRow = (rowObject, index) => {
    const isSelectedRow = index === selectedRow?.index && selectedRow.page === currentPage;
    const shouldApplySelectedRowStyle = isSelectedRow && applyStyleChange;

    const objectValues = [];
    for (const property in rowObject) {
      if (!hiddenColumns.includes(property) && !filteredColumns.includes(property)) {
        objectValues.push({ key: property, value: rowObject[property] });
      }
    }

    let tableDataElements = [];
    if (filteredTableData.length > 0) {
      tableDataElements = objectValues.map((cell, cellIndex) => {
        let tableData = '';
        if (cell && cell.value !== null && cell.value !== undefined) {
          tableData = cell.value.toString();
        }

        const columnStyle = {
          width: (columnWidths && columnWidths[cell.key]) || undefined,
          minWidth: horizontalScroll && columnWidths ? columnWidths[cell.key] : undefined,
        };

        if (tableData === '') {
          return <td style={columnStyle} key={cellIndex} />;
        }

        if (tableData === 'true') {
          return (
            <td style={columnStyle} key={cellIndex}>
              <FontAwesomeIcon icon={faCheck} />
            </td>
          );
        }

        if (tableData === 'false') {
          return (
            <td style={columnStyle} key={cellIndex}>
              <FontAwesomeIcon icon={faTimes} />
            </td>
          );
        }

        if (tableData.includes('00:00')) {
          return (
            <td style={columnStyle} key={cellIndex}>
              {formatDateAsString(new Date(tableData))}
            </td>
          );
        }

        return (
          <td style={columnStyle} key={cellIndex}>
            {tableData}
          </td>
        );
      });
    }

    const isOngoing = rowObject?.dateEnded === 'Ongoing';

    let actionColumnContent = null;
    if (actionColumnEnabled) {
      actionColumnContent = (
        <td className="action-icons" style={{ width: ACTIONS_COL_WIDTH, minWidth: ACTIONS_COL_WIDTH }}>
          {actionOneClick && (
            <FontAwesomeIcon
              color={'#077187'}
              className="action-icon"
              icon={faEdit}
              onClick={(event) => handleActionOneClick(rowObject, event)}
            />
          )}
          {actionTwoClick && (
            <FontAwesomeIcon
              color={'#077187'}
              className="action-icon"
              icon={faEye}
              onClick={(event) => handleActionTwoClick(rowObject, event, index)}
            />
          )}
        </td>
      );
    }

    return (
      <tr
        key={index}
        className={`table-body-row ${shouldApplySelectedRowStyle ? 'selectedRow' : ''} ${
          isOngoing ? 'ongoing-row' : ''
        } ${stickyFirstRow && index === -1 ? 'sticky-row' : ''}`}
        onClick={() => handleRowClick(rowObject, index)}
        style={{ backgroundColor: highlightExpression && highlightExpression(rowObject) ? highlightColor : undefined }}
      >
        {tableDataElements}
        {actionColumnContent}
      </tr>
    );
  };

  const buildTableBody = () => {
    const rows = tableDataPages[currentPage].map(buildRow);

    if (stickyFirstRow) {
      rows.unshift(buildRow(tableData[0], -1));
    }

    return <tbody className="table-body">{rows}</tbody>;
  };

  const tableHeader = buildTableHeader();
  const tableBody = tableDataPages[currentPage] ? buildTableBody() : null;

  return (
    <div className="table-container">
      {(tableTitle || dropdownOptions) && (
        <div className="table-title">
          {tableTitle}
          {dropdownOptions && (
            <Select
              value={dropdownOption}
              options={dropdownOptions}
              onChange={onDropdownChange}
              className="table-select"
              classNamePrefix="table-select"
              getOptionLabel={getDropdownOptionLabel}
              getOptionValue={getDropdownOptionValue}
            />
          )}
          <div className="table-header-button-container">
            {buttonText && (
              <StandardButton
                text={buttonText}
                onClick={onButtonClick}
                disabled={buttonDisabled}
                required={true}
                width={buttonOneWidth}
              />
            )}
            {buttonTwoText && (
              <StandardButton
                text={buttonTwoText}
                onClick={onButtonTwoClick}
                disabled={buttonTwoDisabled}
                required={true}
                width={buttonTwoWidth}
              />
            )}
            {buttonThreeText && (
              <StandardButton
                text={buttonThreeText}
                onClick={onButtonThreeClick}
                disabled={buttonThreeDisabled}
                required={true}
              />
            )}
          </div>
        </div>
      )}

      {tableData.length > 0 && (
        <div className="table-control-container">
          <div className="table-search-filter-container">
            <div className={'search-wrapper'}>
              {searchEnabled && (
                <TableSearch
                  dataToFilter={tableData}
                  setFilteredData={setFilteredTableData}
                  setCurrentPage={setCurrentPage}
                  hiddenColumns={hiddenColumns}
                  searchBarWidth={'40%'}
                  inputWidth={'calc(100% - 40px)'}
                  margin={'7px'}
                  searchIconAlignment={'50px'}
                  stickyFirstRow={stickyFirstRow}
                />
              )}
            </div>

            {filterEnabled && (
              <div className={'filter-wrapper'}>
                <TableFilter
                  dataToFilter={tableData}
                  setFilteredData={setFilteredTableData}
                  hiddenColumns={hiddenColumns}
                  setCurrentPage={setCurrentPage}
                  filteredColumns={filteredColumns}
                  setFilteredColumns={setFilteredColumns}
                  excludedFilterColumns={excludedFilterColumns}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="table-scroll" style={{ overflowX: horizontalScroll ? 'auto' : 'initial' }}>
        <table className="table">
          {tableHeader}
          {tableBody}
        </table>
      </div>

      {filteredTableData.length === 0 && (
        <div className="table-no-results-container">{noResultsMessage ? noResultsMessage : 'No Results Found'}</div>
      )}

      {filteredTableData.length > 0 && (
        <Pagination pageCount={tableDataPages.length} currentPage={currentPage} setCurrentPage={setCurrentPage} />
      )}
    </div>
  );
};
